/**
 * QUIPWA — App root
 */
import { Suspense, useEffect } from 'react'
import { HashRouter, Navigate, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './hooks/AuthProvider.tsx'
import { DBProvider } from './hooks/DBProvider'
import { ErrorBoundary } from './components/ui/ErrorBoundary.tsx'
import { AppShell } from './components/ui/AppShell.tsx'
import { AuthCallbackPage, OnboardingPage, ReviewSessionPage, SessionPage } from './routes/pages.ts'
import { shellRouteElements } from './routes/shell-routes.tsx'
import { lazyPage } from './routes/lazy-page.ts'
import { RouteFallback } from './routes/RouteFallback.tsx'

import { ThemeInjector } from './components/theme/ThemeInjector.tsx'
import { AppBackground } from './components/ui/AppBackground.tsx'
import { Toaster } from './components/system/Toaster.tsx'
import { DialogHost } from './components/system/DialogHost.tsx'
import { AccountMismatchDialog } from './components/system/AccountMismatchDialog.tsx'

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1 } } })

/**
 * Visual QA sandbox with seeded data (src/pages/DesignLabRoutes.tsx).
 * `import.meta.env.DEV` is the literal `false` in a production build, so the
 * import below is dead code there and none of the Design Lab ships.
 */
const DesignLabRoutes = import.meta.env.DEV
  ? lazyPage(() => import('./pages/DesignLabRoutes.tsx'), 'DesignLabRoutes')
  : null

export default function App() {
  useEffect(() => {
    // Global handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[QUIPWA] Unhandled promise rejection:', event.reason)
      event.preventDefault() // Prevent default browser handling
    }

    // Global handler for uncaught errors
    const handleError = (event: ErrorEvent) => {
      console.error('[QUIPWA] Global error:', event.error)
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    window.addEventListener('error', handleError)

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('error', handleError)
    }
  }, [])

  return (
    <ErrorBoundary level="critical">
      <QueryClientProvider client={qc}>
        <DBProvider>
          <AuthProvider>
            <ThemeInjector />
            <AppBackground>
              <HashRouter>
              {/* Full-screen routes suspend here; AppShell has its own boundary around the page. */}
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Pantallas sin AppShell */}
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />

                {/* Sesión activa: fullscreen, sin sidebar */}
                <Route path="deck/:id/session" element={<SessionPage />} />
                {/* «Repasar ahora»: todo lo pendiente de todos los mazos */}
                <Route path="review" element={<ReviewSessionPage />} />

                {DesignLabRoutes && <Route path="design-lab/*" element={<DesignLabRoutes />} />}

                {/* Pantallas con AppShell (sidebar visible): src/routes/pages.ts */}
                <Route element={<AppShell />}>
                  {shellRouteElements()}
                </Route>

                {/* Old bookmarks and, in production, /design-lab/… land on Inicio instead of a blank page. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </HashRouter>
            </AppBackground>
            <Toaster />
            <DialogHost />
            <AccountMismatchDialog />
          </AuthProvider>
        </DBProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
